import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

// Espelha RotaDto do backend (API/Udisys.Api/DTOs/CadastrosGerais/RotasDtos.cs)
export interface Route {
  id: number;
  descricao: string | null;
  referencia: string | null;
  isInativo: boolean;
}

// Espelha RotaComboDto (GET /api/rotas — apenas rotas ativas)
export interface RouteCombo {
  id: number;
  descricao: string | null;
}

// Espelha SalvarRotaRequest
export interface RouteInsert {
  descricao: string;
  referencia?: string | null;
  isInativo?: boolean;
}

interface PaginadoResponse<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
}

export const useRoutes = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["routes"],
    queryFn: async () => {
      const { data } = await apiClient.get<PaginadoResponse<Route>>("/api/cadastros/rotas", {
        params: { tamanhoPagina: 100 },
      });
      return data.itens;
    },
  });

  const activeQuery = useQuery({
    queryKey: ["routes", "active"],
    queryFn: async () => {
      const { data } = await apiClient.get<RouteCombo[]>("/api/rotas");
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (body: RouteInsert) => {
      const { data } = await apiClient.post<Route>("/api/cadastros/rotas", body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["routes"] });
      toast.success("Rota criada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar rota: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: RouteInsert }) => {
      await apiClient.put(`/api/cadastros/rotas/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["routes"] });
      toast.success("Rota atualizada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar rota: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/cadastros/rotas/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["routes"] });
      toast.success("Rota excluída com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir rota: ${error.message}`);
    },
  });

  return {
    routes: query.data,
    activeRoutes: activeQuery.data,
    isLoading: query.isLoading || activeQuery.isLoading,
    error: query.error || activeQuery.error,
    createRoute: createMutation.mutate,
    updateRoute: updateMutation.mutate,
    deleteRoute: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
